import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Globe,
  ShoppingCart,
  MessageSquare,
  BarChart3,
  Settings,
  Activity,
  Menu,
  X,
  Search,
  Bell,
  Target,
  GitBranch,
  FileText,
  Link,
  Cpu,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: '仪表盘' },
  { to: '/sites', icon: Globe, label: '站点管理' },
  { to: '/products', icon: ShoppingCart, label: '产品管理' },
  { to: '/keywords', icon: Target, label: '关键词' },
  { to: '/content', icon: FileText, label: '内容管理' },
  { to: '/prompts', icon: MessageSquare, label: 'Prompt管理' },
  { to: '/affiliate', icon: Link, label: '联盟营销' },
  { to: '/ai-settings', icon: Cpu, label: 'AI 模型' },
  { to: '/workflows', icon: GitBranch, label: '工作流' },
  { to: '/analytics', icon: BarChart3, label: '统计分析' },
  { to: '/realtime', icon: Activity, label: '实时访客' },
  { to: '/settings', icon: Settings, label: '系统设置' },
];

const mobileNavItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: '首页' },
  { to: '/sites', icon: Globe, label: '站点' },
  { to: '/products', icon: ShoppingCart, label: '产品' },
  { to: '/keywords', icon: Target, label: '关键词' },
  { to: '/content', icon: FileText, label: '内容' },
  { to: '/affiliate', icon: Link, label: '联盟' },
  { to: '/ai-settings', icon: Cpu, label: 'AI' },
  { to: '/workflows', icon: GitBranch, label: '工作流' },
  { to: '/settings', icon: Settings, label: '设置' },
];

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop sidebar - hidden on mobile */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-gray-200">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar drawer */}
      <div className={cn(
        'lg:hidden fixed inset-0 z-50 transition-opacity duration-300',
        sidebarOpen ? 'visible' : 'invisible'
      )}>
        <div
          className={cn(
            'absolute inset-0 bg-gray-900/50 transition-opacity duration-300',
            sidebarOpen ? 'opacity-100' : 'opacity-0'
          )}
          onClick={() => setSidebarOpen(false)}
        />
        <div className={cn(
          'absolute inset-y-0 left-0 w-72 bg-white shadow-xl transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}>
          <SidebarContent onClose={() => setSidebarOpen(false)} mobile />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
        <MobileNav />
      </div>
    </div>
  );
}

function SidebarContent({ onClose, mobile }: { onClose?: () => void; mobile?: boolean }) {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 md:p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900">
              Affiliate Manager
            </h1>
            <p className="text-xs md:text-sm text-gray-500">Cloudflare Edition</p>
          </div>
          {mobile && onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <nav className="flex-1 p-3 md:p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) => cn(
              'flex items-center gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-all',
              isActive
                ? 'bg-primary-50 text-primary-600 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            )}
          >
            <item.icon className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
            <span className="font-medium text-sm md:text-base">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="hidden lg:block p-4 border-t border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white font-medium">A</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">Admin</p>
            <p className="text-xs text-gray-500">Cloudflare安全</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Header({ onMenuClick }: { onMenuClick: () => void }) {
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-14 md:h-16 px-4 md:px-6">
        <div className="flex items-center gap-3 flex-1">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="hidden md:flex items-center flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="搜索站点、产品、Prompt..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <button className="relative p-2 rounded-lg hover:bg-gray-100" aria-label="Notifications">
            <Bell className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>

          <div className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">A</span>
            </div>
            <span className="hidden md:block text-sm font-medium text-gray-700">Admin</span>
          </div>
        </div>
      </div>

      <div className="md:hidden px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="搜索..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          />
        </div>
      </div>
    </header>
  );
}

function MobileNav() {
  const location = useLocation();

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-safe">
      <div className="flex items-center justify-around h-14">
        {mobileNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => cn(
              'flex flex-col items-center justify-center flex-1 h-full transition-colors',
              isActive
                ? 'text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs mt-1">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

// Add pb-safe utility
const style = document.createElement('style');
style.textContent = `
  .pb-safe {
    padding-bottom: env(safe-area-inset-bottom, 20px);
  }
`;
document.head.appendChild(style);
